import React, { useState, useRef, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Currency, SUPPORTED_CURRENCIES } from '../../types/currency';
import { useCurrency } from '../../context/CurrencyContext';
import DirhamSymbol from '../DirhamSymbol';
import styles from './CurrencySelector.module.scss';

/** Render the currency symbol — SVG for AED, plain text for everything else. */
const CurrencySymbolDisplay: React.FC<{ currency: Currency; size?: string }> = ({ currency, size = '1em' }) =>
  currency.code === 'AED' ? (
    <DirhamSymbol size={size} style={{ verticalAlign: 'middle' }} />
  ) : (
    <span className={styles.symbol}>{currency.symbol}</span>
  );

interface CurrencySelectorProps {
  className?: string;
  showLabel?: boolean;
  variant?: 'navbar' | 'inline';
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  className = '',
  showLabel = false,
  variant = 'navbar'
}) => {
  const { currencyState, changeCurrency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter currencies based on search term
  const filteredCurrencies = SUPPORTED_CURRENCIES.filter(currency =>
    currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    currency.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCurrencySelect = (currency: Currency) => {
    changeCurrency(currency);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div ref={dropdownRef} className={`${styles.currencySelector} ${styles[variant]} ${className}`}>
      {showLabel && (
        <span className={styles.label}>Currency:</span>
      )}
      
      <Dropdown show={isOpen} onToggle={toggleDropdown}>
        <Dropdown.Toggle
          variant="outline-secondary"
          className={styles.currencyToggle}
          onClick={toggleDropdown}
        >
          <span className={styles.currencyDisplay}>
  <CurrencySymbolDisplay currency={currencyState.selectedCurrency} size="1.1em" />
  <span className={styles.code}>{currencyState.selectedCurrency.code}</span>
</span>
        </Dropdown.Toggle>

        <Dropdown.Menu className={styles.currencyMenu}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search currencies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
              autoFocus={isOpen}
            />
          </div>

          <div className={styles.currencyList}>
            {filteredCurrencies.length > 0 ? (
              filteredCurrencies.map((currency) => (
                <Dropdown.Item
                  key={currency.code}
                  className={`${styles.currencyOption} ${
                    currency.code === currencyState.selectedCurrency.code ? styles.selected : ''
                  }`}
                  onClick={() => handleCurrencySelect(currency)}
                >
                  <div className={styles.currencyInfo}>
                    <span className={styles.flag}>{currency.flag}</span>
                    <div className={styles.currencyDetails}>
                      <div className={styles.currencyMain}>
                        <span className={styles.code}>{currency.code}</span>
                        <CurrencySymbolDisplay currency={currency} size="1em" />
                      </div>
                      <span className={styles.name}>{currency.name}</span>
                    </div>
                  </div>
                  {currency.code === currencyState.selectedCurrency.code && (
                    <span className={styles.checkmark}>✓</span>
                  )}
                </Dropdown.Item>
              ))
            ) : (
              <div className={styles.noResults}>
                No currencies found matching "{searchTerm}"
              </div>
            )}
          </div>

          {currencyState.lastUpdated && (
            <div className={styles.lastUpdated}>
              Last updated: {currencyState.lastUpdated.toLocaleTimeString()}
            </div>
          )}

          {currencyState.error && (
            <div className={styles.error}>
              {currencyState.error}
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {currencyState.isLoading && (
        <div className={styles.loadingIndicator}>
          <span className={styles.spinner}></span>
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;